import moment from 'moment';
import { ChapterResponse } from '@goldcast/api/content';
import {
  SearchParagraph,
  SearchParagraphWord,
  SearchResult,
  SpeakerRowDetails,
  TranscriptParagraph,
  TranscriptSelectionRange,
  TranscriptSelectionRanges
} from './types';
import { CLIP_MAX_DURATION_ERROR, CLIP_MAX_DURATION, CLIP_MIN_DURATION, CLIP_MIN_DURATION_ERROR } from './constants';
import { WordType, type Word } from '@/domains/transcript';
import { formatTime } from '@/libs/core';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import { Clip, ClipDeletes, ClipEdits } from '@/domains/asset';
import { addDecimalIfNeeded, roundToNDecimalPlaces } from '@/libs/utils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import {
  getDeletesForSegment,
  getEditsForSegment,
  getEndForSegment,
  getMergesForSegment,
  getStartForSegment
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import {
  getAllCompositeSegments,
  getAllSegments
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import {
  getPlayerTimeFromSegmentTime,
  getSegmentAtCurrentFrame,
  getSegmentTimeFromPlayerTime
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { approxEquals, approxGT, approxGTE, approxLT, approxLTE } from '@/libs/compare';
import { getClipFPS } from '@/libs/clipUtils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { VideoSegment } from '@/App/remotion/MainPlayer/types/Segment/VideoSegment';

export function getSpeakerRowDetails(word: Word, speakers: Record<string, SpeakerWithDetails>): SpeakerRowDetails {
  return {
    speaker_label: word.speaker_label,
    speaker: speakers[word.speaker_label],
    start: formatTime(word.start_time),
    end: formatTime(speakers[word.speaker_label].speakingSlots[word.start_time])
  };
}

export function getSelectionDuration(words: Record<string, Word[]>): number | string {
  if (Object.keys(words).length === 0) {
    return 0;
  }
  const duration = Object.entries(words)
    .filter(([_segmentId, words]) => words.length > 0)
    .reduce((acc, [_segmentId, words]) => {
      return acc + words[words.length - 1].end_time - words[0].start_time;
    }, 0);
  if (duration < CLIP_MIN_DURATION) {
    return CLIP_MIN_DURATION_ERROR;
  }
  if (duration > CLIP_MAX_DURATION) {
    return CLIP_MAX_DURATION_ERROR;
  }
  if (duration < 60) {
    return `${duration.toFixed(2)} sec`;
  }
  if (duration < 3600) {
    return moment.utc(duration * 1000).format('mm [min] ss [sec]');
  }
  return moment.utc(duration * 1000).format('H [hr] mm [min] ss [sec]');
}

export function getEditTranscriptDuration(duration: number): number | string {
  duration = roundToNDecimalPlaces(duration, 2);
  if (duration < 60) {
    return `${duration}s`;
  }
  if (duration < 3600) {
    return moment.utc(duration * 1000).format('mm[m] ss[s]');
  }
  return moment.utc(duration * 1000).format('H[h] mm[m]');
}

export function getWordIdFromElement(element: HTMLElement): number {
  return parseInt(element.dataset.wordId as string);
}

export function getWordStartTimeFromElement(element: HTMLElement): number | null {
  try {
    const wordStartTime = parseFloat(element.dataset.wordStartTime as string);
    return wordStartTime;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export function getParagraphIndexByTime(
  time: number,
  segmentParagraphs: TranscriptParagraph[],
  segmentId: string
): number {
  for (let i = 0; i < segmentParagraphs.length; i++) {
    if (segmentParagraphs[i].segmentId !== segmentId) {
      continue;
    }
    if (segmentParagraphs[i].videoSegmentDetails) {
      return i;
    }
    if (!segmentParagraphs[i].words?.length) {
      continue;
    }
    const wordIndex = segmentParagraphs[i].words.findIndex(w => w.start_time === time);
    if (wordIndex >= 0) {
      return i;
    }
  }
  return -1;
}

export function convertParagraphToSearchParagraph(words: Word[], index: number, segmentId: string): SearchParagraph {
  let content = '';
  const finalWords: Array<SearchParagraphWord> = [];
  let cursor = 0;
  words.forEach((w: Word) => {
    content += w.content + ' ';
    finalWords.push({
      content: w.content,
      startIndex: cursor,
      endIndex: cursor + w.content.length,
      start_time: w.start_time,
      end_time: w.end_time,
      index: w.index
    });
    cursor += w.content.length + 1;
  });

  return { content, words: finalWords, index, segmentId };
}

export function getParagraphSearchResults(paragraph: SearchParagraph, query: string): SearchResult[] {
  // replace regex special characters
  const regExpQuery = query.toLowerCase().replace(/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/, '\\$&');
  const matches = [...paragraph.content.toLowerCase().matchAll(new RegExp(regExpQuery, 'g'))];
  if (!matches.length) {
    return [];
  }
  return matches.map((match: RegExpMatchArray) => {
    const start = match.index ?? 0;
    return {
      index: paragraph.index,
      segmentId: paragraph.segmentId,
      start,
      end: start + query.length,
      paragraph: paragraph.content,
      words: paragraph.words
    };
  });
}

function getIsNonWordParagraph(paragraph): boolean {
  const isChapterParagraph = !!paragraph.chapterDetails;
  const isSpeakerParagraph = paragraph.speakerDetails !== undefined;
  const isVideoSegmentParagraph = !!paragraph.videoSegmentDetails;
  return isChapterParagraph || isSpeakerParagraph || isVideoSegmentParagraph;
}

class SentenceBreaker {
  private sentenceCount: number;
  private rollingWordCount: number;
  private transcript: Record<string, Word[]>;
  private isTranslated: boolean;

  constructor(transcript: Record<string, Word[]>, isTranslated = false) {
    this.reset();
    this.transcript = transcript;
    this.isTranslated = isTranslated;
  }

  isWordFollowedByBreak(compositeSegmentId: string, index: number): boolean {
    return this.transcript[compositeSegmentId][index + 1]
      ? this.isLineBreak(this.transcript[compositeSegmentId][index + 1])
      : false;
  }

  private isLineBreak(word: Word): boolean {
    if (
      !this.isTranslated &&
      word.type === WordType.Punctuation &&
      word.content.match(/[.?!]/g) &&
      this.rollingWordCount > 5
    ) {
      this.sentenceCount--;
    }
    if (this.isTranslated && (word.content.endsWith('.') || word.content.endsWith('!') || word.content.endsWith('?'))) {
      this.sentenceCount--;
    }
    if (this.sentenceCount === 0) {
      this.reset();
      return true;
    }
    this.rollingWordCount++;
    return false;
  }

  private reset() {
    this.sentenceCount = 2;
    this.rollingWordCount = 0;
  }
}
const getChapterWithSegmentTime = (chapter: ChapterResponse[], clip?: Clip) => {
  if (!clip) {
    return null;
  }
  const isSplitsEnabled = isSplitsInTimelineEnabled(clip);
  const allCompositeSegments = getAllCompositeSegments(clip);
  return chapter.map(chapter => {
    const currentSegment = getSegmentAtCurrentFrame(chapter.start_time, clip);
    const segmentTime =
      currentSegment && isSplitsEnabled
        ? getSegmentTimeFromPlayerTime(chapter.start_time, clip, currentSegment.id)
        : chapter.start_time;
    return {
      ...chapter,
      start_time: segmentTime,
      segmentId: currentSegment?.id || allCompositeSegments[0]?.id || ''
    };
  });
};

export function parseTranscriptToParagraphs(
  transcript: Record<string, Word[]>,
  speakersWithDetails: Record<string, SpeakerWithDetails>,
  clip: Clip | undefined,
  chapters: { id: string; title: string; start_time: number }[] = [],
  isTranslated: boolean = false,
  transcriptExtension: { startTime: number; startSegmentId: string } | { endTime: number; endSegmentId: string } = {
    startTime: 0,
    endTime: 0,
    startSegmentId: '',
    endSegmentId: ''
  }
): TranscriptParagraph[] {
  const allParagraphs: TranscriptParagraph[] = [] as TranscriptParagraph[];
  let currentParagraphWords: Word[] = [];
  let currentSpeaker: SpeakerRowDetails;
  let chapterIndex = 0;
  const isSplitsEnabled = !!clip && isSplitsInTimelineEnabled(clip);
  const isChaptersEnabled = !!clip;

  const allCompositeSegments = clip ? getAllCompositeSegments(clip) : [];

  const sentenceBreaker = new SentenceBreaker(transcript, isTranslated);

  const chaptersWithSegmentTime = getChapterWithSegmentTime(chapters, clip);

  const checkAndAddParagraphForChapter = (startTime: number, segmentId: string) => {
    while (
      isChaptersEnabled &&
      chapterIndex < chapters.length &&
      chaptersWithSegmentTime &&
      chaptersWithSegmentTime[chapterIndex].segmentId === segmentId &&
      approxLTE(chaptersWithSegmentTime[chapterIndex].start_time, startTime)
    ) {
      const chapter = chapters[chapterIndex];
      // Push the current paragraph if it has words
      if (currentParagraphWords.length) {
        allParagraphs.push({ words: currentParagraphWords, segmentId: segmentId });
        currentParagraphWords = [];
      }
      // Add chapter as a new paragraph
      allParagraphs.push({
        segmentId: segmentId,
        chapterDetails: {
          id: chapter.id,
          title: chapter.title,
          start: chapter.start_time,
          end: chapters[chapterIndex + 1]?.start_time
        },
        words: []
      });
      chapterIndex++;
    }
  };

  if (!clip) {
    return allParagraphs;
  }
  Object.entries(transcript).forEach(([segmentId, words]) => {
    const compositeSegment = allCompositeSegments.find(segment => segment.id === segmentId);

    if (!compositeSegment) {
      const allSegments = getAllSegments(clip);
      const videoSegment = allSegments.find(
        segment => segment.type === SegmentType.VIDEO && segment.id === segmentId
      ) as VideoSegment;
      if (!videoSegment || !isSplitsEnabled) {
        return;
      }
      const durationInFrames = (videoSegment?.toFrame || 0) - (videoSegment?.fromFrame || 0);
      checkAndAddParagraphForChapter(0, segmentId);
      allParagraphs.push({
        segmentId: segmentId,
        words: [],
        videoSegmentDetails: {
          id: segmentId,
          name: videoSegment?.video.name || '',
          fromFrame: videoSegment?.fromFrame || 0,
          toFrame: videoSegment?.toFrame || 0,
          duration: durationInFrames / getClipFPS(clip)
        }
      });
      checkAndAddParagraphForChapter(durationInFrames / getClipFPS(clip), segmentId);

      return;
    }
    let segmentStartTime = getStartForSegment(clip, compositeSegment.id);
    let segmentEndTime = getEndForSegment(clip, compositeSegment.id);

    if ('startSegmentId' in transcriptExtension && transcriptExtension.startSegmentId === segmentId) {
      segmentStartTime = Math.min(segmentStartTime, transcriptExtension.startTime);
    }
    if ('endSegmentId' in transcriptExtension && transcriptExtension.endSegmentId === segmentId) {
      segmentEndTime = Math.max(segmentEndTime, transcriptExtension.endTime);
    }

    let nonTrimmedWordIndex = -1;

    words.forEach((word, index) => {
      if (
        isSplitsEnabled &&
        !(approxGT(word.end_time, segmentStartTime) && approxLT(word.start_time, segmentEndTime))
      ) {
        return;
      }
      nonTrimmedWordIndex += 1;
      checkAndAddParagraphForChapter(word.start_time, segmentId);
      // Add new speaker row
      if (nonTrimmedWordIndex === 0 || word.speaker_label !== currentSpeaker?.speaker_label) {
        // Next speaker, save current paragraph
        if (currentParagraphWords.length) {
          allParagraphs.push({
            words: currentParagraphWords,
            segmentId: segmentId
          });
          currentParagraphWords = [];
        }
        currentSpeaker = getSpeakerRowDetails(word, speakersWithDetails);
        allParagraphs.push({
          speakerDetails: currentSpeaker,
          words: [],
          segmentId: segmentId
        });
      }
      // Add words to the current paragraph
      const isBreakFollowing = sentenceBreaker.isWordFollowedByBreak(segmentId, index);
      if (word.type !== WordType.Punctuation) {
        const parsedWord = parseWord(words, word, index, clip, segmentId);
        if (!parsedWord) {
          return;
        }
        currentParagraphWords.push(parsedWord);
        if (isBreakFollowing || nonTrimmedWordIndex === words.length - 1) {
          allParagraphs.push({
            words: currentParagraphWords,
            segmentId: segmentId
          });
          currentParagraphWords = [];
        }
      }
    });
    // If there is remaining paragraph that is not ending with punctuation
    if (currentParagraphWords.length) {
      allParagraphs.push({
        words: currentParagraphWords,
        segmentId: segmentId
      });
    }
    currentParagraphWords = [];
  });

  let previousWordParagraph: TranscriptParagraph | undefined = undefined;
  allParagraphs.forEach(paragraph => {
    const isNonWordParagraph = getIsNonWordParagraph(paragraph);
    if (isNonWordParagraph) {
      return;
    }
    const newSegmentStart = previousWordParagraph?.segmentId !== paragraph.segmentId;

    if (newSegmentStart) {
      if (previousWordParagraph) {
        const previousSegmentId = previousWordParagraph.segmentId;
        let segmentEndTime = getEndForSegment(clip, previousSegmentId);

        if ('endSegmentId' in transcriptExtension && transcriptExtension.endSegmentId === previousSegmentId) {
          segmentEndTime = Math.max(segmentEndTime, transcriptExtension.endTime);
        }

        const previousSegmentWords = transcript[previousSegmentId];
        const hasSomeDeletedAfter =
          previousSegmentWords.some(w => approxGT(w.end_time, segmentEndTime)) && isSplitsEnabled;
        previousWordParagraph.hasSomeDeletedAfter = hasSomeDeletedAfter;
      }

      const segmentId = paragraph.segmentId;
      let segmentStartTime = getStartForSegment(clip, segmentId);

      if ('startSegmentId' in transcriptExtension && transcriptExtension.startSegmentId === segmentId) {
        segmentStartTime = Math.min(segmentStartTime, transcriptExtension.startTime);
      }

      const segmentWords = transcript[segmentId];
      const hasSomeDeletedBefore = segmentWords.some(w => approxLT(w.start_time, segmentStartTime)) && isSplitsEnabled;
      paragraph.hasSomeDeletedBefore = hasSomeDeletedBefore;
    }

    previousWordParagraph = paragraph;
  });

  const lastParagraph = allParagraphs[allParagraphs.length - 1];
  if (lastParagraph) {
    const lastSegmentId = lastParagraph.segmentId;
    const lastSegmentWords = transcript[lastSegmentId];
    let segmentEndTime = getEndForSegment(clip, lastSegmentId);

    if ('endSegmentId' in transcriptExtension && transcriptExtension.endSegmentId === lastSegmentId) {
      segmentEndTime = Math.max(segmentEndTime, transcriptExtension.endTime);
    }

    const hasSomeDeletedAfter = lastSegmentWords.some(w => approxGT(w.end_time, segmentEndTime)) && isSplitsEnabled;
    lastParagraph.hasSomeDeletedAfter = hasSomeDeletedAfter;
  }

  return allParagraphs;
}

function parseWord(
  segmentWords: Word[],
  word: Word,
  index: number,
  clip: Clip,
  compositeSegmentId: string
): Word | null {
  let { content, end_time } = word;
  let isHidden = false;

  const clipMerges = getMergesForSegment(clip, compositeSegmentId);
  const clipDeletes = getDeletesForSegment(clip, compositeSegmentId);
  const clipEdits = getEditsForSegment(clip, compositeSegmentId);

  // Only set is_filler if the feature flag is enabled
  const canUseFillerWords = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.USE_CL_FILLER_WORDS];

  const isFiller = canUseFillerWords ? word?.is_filler : false;

  if (segmentWords[index + 1]?.type === WordType.Punctuation) {
    content += segmentWords[index + 1].content;
  }

  if (clipMerges) {
    const currentMerge = clipMerges.find(m => word.start_time >= m.start_time && word.end_time <= m.end_time);
    if (currentMerge) {
      if (currentMerge.start_time === word.start_time) {
        content = currentMerge.word;
        end_time = currentMerge.end_time;
      } else {
        return null;
      }
    }
  }

  if (clipEdits) {
    ({ content, isHidden } = getEditedWordContent(word, content, clipEdits, isHidden || false));
  }

  const isDeleted = !!clipDeletes && isWordDeleted(word, clipDeletes);

  return {
    ...word,
    is_deleted: isDeleted,
    hidden: isHidden,
    is_filler: isFiller,
    end_time,
    content
  };
}

export function isWordDeleted(word: Word, clipDeletes: ClipDeletes): boolean {
  return !!Object.values(clipDeletes).find(
    val => approxGTE(word.start_time, val.bounds[0]) && approxLTE(word.end_time, val.bounds[1])
  );
}

function getEditedWordContent(
  word: Word,
  content: string,
  clipEdits: ClipEdits,
  isHidden: boolean
): { content: string; isHidden: boolean } {
  const startTime: string = addDecimalIfNeeded(word.start_time);
  const edit: { word?: string } = clipEdits[startTime];

  if (edit) {
    // If there is edit with content -> override. Otherwise, no content, word is hidden
    return edit.word ? { content: edit.word, isHidden: false } : { content, isHidden: true };
  }

  return { content, isHidden: isHidden || false };
}

/**
 * Determines the active words and deleted words ranges within a transcript selection of words based on clip deletions.
 *
 * @param words - An array of Word objects representing the selected words in the transcript.
 * @param clipDeletes - A record of deleted ranges in the clip, keyed by an identifier.
 * @returns An object containing two arrays: 'deleted' and 'active', each holding TranscriptSelectionRange objects.
 *          These ranges represent contiguous segments of deleted or active words in the selection.
 */
export function getSelectionRanges(words: Word[], clipDeletes: ClipDeletes): TranscriptSelectionRanges {
  // If no clip deletes, no deleted ranges
  if (!clipDeletes) {
    return {
      deleted: [],
      active: [{ start: words[0].start_time, end: words[words.length - 1].end_time }]
    };
  }

  const ranges: TranscriptSelectionRanges = { deleted: [], active: [] };
  let currentRange: TranscriptSelectionRange | null = null;
  let currentStatus: 'active' | 'deleted' | null = null;

  for (const word of words) {
    const wordStatus = isWordDeleted(word, clipDeletes) ? 'deleted' : 'active';
    if (!currentStatus || wordStatus !== currentStatus) {
      if (!!currentStatus && !!currentRange) {
        ranges[currentStatus].push(currentRange);
      }
      currentStatus = wordStatus;
      currentRange = { start: word.start_time, end: word.end_time };
    } else if (currentRange && currentStatus === wordStatus) {
      currentRange.end = word.end_time;
    }
  }
  // Add last range
  if (!!currentStatus && !!currentRange) {
    ranges[currentStatus].push(currentRange);
  }

  return ranges;
}

export function timeSpeakerDetailsStringToSeconds(timeString: string): number {
  const components: number[] = timeString.split(':').map(parseFloat).reverse();
  const [seconds = 0, minutes = 0, hours = 0] = components;
  return hours * 3600 + minutes * 60 + seconds;
}

export function getActiveWords({
  currentTime,
  transcript,
  clipData
}: {
  currentTime: number;
  transcript: Record<string, Word[]>;
  clipData: Clip;
}) {
  const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
  if (!currentSegment || currentSegment.type !== SegmentType.COMPOSITE) {
    return { activeWords: [], currentSegment: currentSegment || null };
  }
  const compositeSegmentTime = getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);
  const activeWords = transcript[currentSegment.id].filter(
    w =>
      approxLTE(w.start_time, compositeSegmentTime) &&
      approxGT(w.end_time, compositeSegmentTime) &&
      w.type !== WordType.Punctuation &&
      !w.is_deleted
  );

  if (activeWords.length !== 0) {
    return { activeWords, currentSegment };
  }
  const closestWord = transcript[currentSegment.id].reduce(
    (prevWord, currentWord) => {
      if (!prevWord) {
        return undefined;
      }
      return Math.abs(currentWord.start_time - compositeSegmentTime) <
        Math.abs(prevWord.start_time - compositeSegmentTime) &&
        currentWord.type !== WordType.Punctuation &&
        !currentWord.is_deleted
        ? currentWord
        : prevWord;
    },
    transcript[currentSegment.id].find(w => !w.is_deleted)
  );

  return closestWord && closestWord.index >= 0
    ? { activeWords: [closestWord], currentSegment }
    : { activeWords: [], currentSegment };
}

export const getStartTimeAndEndTime = (anchorNode: Node | null, focusNode: Node | null) => {
  const anchorNodeStartTime =
    anchorNode?.parentElement?.closest('[data-word-start-time]')?.getAttribute('data-word-start-time') || null;
  const anchorNodeEndTime =
    anchorNode?.parentElement?.closest('[data-word-end-time]')?.getAttribute('data-word-end-time') || null;
  const focusNodeStartTime =
    focusNode?.parentElement?.closest('[data-word-start-time]')?.getAttribute('data-word-start-time') || null;
  const focusNodeEndTime =
    focusNode?.parentElement?.closest('[data-word-end-time]')?.getAttribute('data-word-end-time') || null;

  const newStartTime =
    anchorNodeStartTime !== null && focusNodeStartTime !== null
      ? Math.min(+anchorNodeStartTime, +focusNodeStartTime)
      : anchorNodeStartTime || focusNodeEndTime;
  const newEndTime =
    anchorNodeEndTime !== null && focusNodeEndTime !== null
      ? Math.max(+anchorNodeEndTime, +focusNodeEndTime)
      : anchorNodeEndTime || focusNodeStartTime;

  if (newStartTime === null || newEndTime === null) {
    return {
      startTime: null,
      endTime: null
    };
  }

  return {
    startTime: +newStartTime,
    endTime: +newEndTime
  };
};

export const getTimeWithoutDeletesForRange = ({
  startTime,
  endTime,
  startSegmentId,
  endSegmentId,
  clipData
}: {
  startTime: number;
  endTime: number;
  startSegmentId: string;
  endSegmentId: string;
  clipData: Clip;
}) => {
  const startPlayerTime = getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);
  const endPlayerTime = getPlayerTimeFromSegmentTime(endTime, clipData, endSegmentId);
  const difference = endPlayerTime - startPlayerTime;
  return approxEquals(difference, 0) ? 0 : difference;
};
